/* ==========================================================================
   Maštarije Nova — mst-core.js
   Foundation runtime. Exposes `window.MST`:

     MST.pubsub.subscribe(event, callback) -> unsubscribe()
     MST.pubsub.publish(event, data)
     MST.fetchConfig(type = 'json')            // headers for Shopify AJAX API
     MST.formatMoney(cents, format?)           // format from body[data-money-format]
     MST.trapFocus(container, elementToFocus?) // keyboard trap for drawers/modals
     MST.releaseFocus(elementToFocus?)         // release + optionally restore focus
     MST.onSectionLoad(callback)               // callback(container, sectionId) on
                                               // shopify:section:load (editor-safe)

   Custom elements:
     <mst-quantity> — plus/minus quantity box. Expected markup:
       <mst-quantity class="mst-quantity">
         <button type="button" name="minus" class="mst-quantity__btn">…</button>
         <input class="mst-quantity__input" type="number" name="quantity" value="1" min="1">
         <button type="button" name="plus" class="mst-quantity__btn">…</button>
       </mst-quantity>

   Document CustomEvents (per contract §5):
     'mst:cart:updated'    detail: { cart }
     'mst:variant:changed' detail: { variant, sectionId } (dispatched by mst-product.js)

   Also here: fade-up scroll reveal (.mst-reveal → .is-revealed, motion-safe).
   ========================================================================== */

(function () {
  'use strict';

  if (window.MST) return;

  /* ------------------------------------------------------------------------
     Pub/sub
     ------------------------------------------------------------------------ */
  var subscribers = {};

  var pubsub = {
    subscribe: function (event, callback) {
      if (!subscribers[event]) subscribers[event] = [];
      subscribers[event].push(callback);
      return function unsubscribe() {
        subscribers[event] = subscribers[event].filter(function (cb) {
          return cb !== callback;
        });
      };
    },
    publish: function (event, data) {
      if (!subscribers[event]) return;
      subscribers[event].forEach(function (callback) {
        try {
          callback(data);
        } catch (error) {
          console.error('[MST.pubsub] subscriber error for "' + event + '"', error);
        }
      });
    }
  };

  /* ------------------------------------------------------------------------
     fetchConfig — headers for Shopify AJAX endpoints
     ------------------------------------------------------------------------ */
  function fetchConfig(type) {
    type = type || 'json';
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/' + type
      }
    };
  }

  /* ------------------------------------------------------------------------
     formatMoney — Shopify money format placeholders
     ------------------------------------------------------------------------ */
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = typeof precision === 'number' ? precision : 2;
    thousands = thousands || ',';
    decimal = decimal || '.';

    if (isNaN(number) || number == null) return '0';

    number = (number / 100).toFixed(precision);

    var parts = number.split('.');
    var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseFloat(cents);

    var moneyFormat =
      format ||
      (document.body && document.body.dataset.moneyFormat) ||
      '€{{amount_with_comma_separator}}';

    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = moneyFormat.match(placeholderRegex);
    var value = '';

    switch (match ? match[1] : 'amount') {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', '');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return moneyFormat.replace(placeholderRegex, value);
  }

  /* ------------------------------------------------------------------------
     Focus trap
     ------------------------------------------------------------------------ */
  var FOCUSABLE_SELECTOR =
    "summary, a[href], button:not([disabled]), [tabindex]:not([tabindex^='-']), " +
    "input:not([type='hidden']):not([disabled]), select:not([disabled]), " +
    'textarea:not([disabled]), iframe, object, area[href]';

  var activeTrap = null;

  function getFocusableElements(container) {
    return Array.prototype.filter.call(
      container.querySelectorAll(FOCUSABLE_SELECTOR),
      function (el) {
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
      }
    );
  }

  function trapFocus(container, elementToFocus) {
    releaseFocus();

    var handlers = {
      focusin: function (event) {
        if (event.target === container || container.contains(event.target)) return;
        var focusable = getFocusableElements(container);
        if (focusable.length) focusable[0].focus();
      },
      keydown: function (event) {
        if (event.key !== 'Tab') return;
        var focusable = getFocusableElements(container);
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === container)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('focusin', handlers.focusin);
    document.addEventListener('keydown', handlers.keydown);
    activeTrap = { container: container, handlers: handlers };

    var target = elementToFocus || container;
    if (target && typeof target.focus === 'function') target.focus();
  }

  function releaseFocus(elementToFocus) {
    if (activeTrap) {
      document.removeEventListener('focusin', activeTrap.handlers.focusin);
      document.removeEventListener('keydown', activeTrap.handlers.keydown);
      activeTrap = null;
    }
    if (elementToFocus && typeof elementToFocus.focus === 'function') {
      elementToFocus.focus();
    }
  }

  /* ------------------------------------------------------------------------
     onSectionLoad — theme editor re-init hook
     ------------------------------------------------------------------------ */
  function onSectionLoad(callback) {
    document.addEventListener('shopify:section:load', function (event) {
      var sectionId = event.detail ? event.detail.sectionId : null;
      callback(event.target, sectionId);
    });
  }

  window.MST = {
    pubsub: pubsub,
    fetchConfig: fetchConfig,
    formatMoney: formatMoney,
    trapFocus: trapFocus,
    releaseFocus: releaseFocus,
    onSectionLoad: onSectionLoad
  };

  /* ------------------------------------------------------------------------
     <mst-quantity>
     ------------------------------------------------------------------------ */
  if (!customElements.get('mst-quantity')) {
    customElements.define(
      'mst-quantity',
      class MstQuantity extends HTMLElement {
        connectedCallback() {
          this.input = this.querySelector('input[name="quantity"]') || this.querySelector('input');
          this.minusButton = this.querySelector('button[name="minus"]');
          this.plusButton = this.querySelector('button[name="plus"]');
          if (!this.input) return;

          this.onClickBound = this.onButtonClick.bind(this);
          this.onChangeBound = this.onInputChange.bind(this);

          if (this.minusButton) this.minusButton.addEventListener('click', this.onClickBound);
          if (this.plusButton) this.plusButton.addEventListener('click', this.onClickBound);
          this.input.addEventListener('change', this.onChangeBound);

          // accessibility-09: on the product page the new amount is spoken
          // ("Količina: 2") — focus stays on the button, so the value
          // changing beside it was silent. The cart announces its own update.
          if (this.dataset.liveLabel && !this.querySelector('[data-quantity-live]')) {
            this.live = document.createElement('span');
            this.live.className = 'visually-hidden';
            this.live.setAttribute('data-quantity-live', '');
            this.live.setAttribute('role', 'status');
            this.live.setAttribute('aria-live', 'polite');
            this.appendChild(this.live);
          } else {
            this.live = this.querySelector('[data-quantity-live]');
          }

          this.updateButtonStates();
        }

        disconnectedCallback() {
          if (this.minusButton) this.minusButton.removeEventListener('click', this.onClickBound);
          if (this.plusButton) this.plusButton.removeEventListener('click', this.onClickBound);
          if (this.input) this.input.removeEventListener('change', this.onChangeBound);
        }

        onButtonClick(event) {
          event.preventDefault();
          var button = event.currentTarget;
          if (button.getAttribute('aria-disabled') === 'true') return;
          var previousValue = this.input.value;

          if (button.name === 'plus') {
            this.input.stepUp();
          } else {
            this.input.stepDown();
          }

          if (previousValue !== this.input.value) {
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
            if (this.live) {
              this.live.textContent = this.dataset.liveLabel + ': ' + this.input.value;
            }
          }
        }

        onInputChange() {
          this.clampValue();
          this.updateButtonStates();
        }

        clampValue() {
          var value = parseInt(this.input.value, 10);
          var min = parseInt(this.input.min, 10);
          var max = parseInt(this.input.max, 10);

          if (isNaN(value)) value = isNaN(min) ? 1 : min;
          if (!isNaN(min) && value < min) value = min;
          if (!isNaN(max) && value > max) value = max;

          this.input.value = value;
        }

        updateButtonStates() {
          var value = parseInt(this.input.value, 10);
          var min = parseInt(this.input.min, 10);
          var max = parseInt(this.input.max, 10);

          // accessibility-09: aria-disabled, not disabled — a disabled button
          // throws keyboard focus back to <body> the moment "−" hits the
          // minimum. The click guard above keeps it inert.
          if (this.minusButton) {
            this.minusButton.setAttribute('aria-disabled', !isNaN(min) && value <= min ? 'true' : 'false');
          }
          if (this.plusButton) {
            this.plusButton.setAttribute('aria-disabled', !isNaN(max) && value >= max ? 'true' : 'false');
          }
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     <mst-localization-form> — language/country selects auto-submit on change
     (the snippet's <noscript> Apply button covers no-JS). Defined here, not
     in a section, because snippets/localization.liquid is rendered by the
     footer AND the menu drawer — it must work whichever sections a merchant
     keeps in the theme editor.
     ------------------------------------------------------------------------ */
  if (!customElements.get('mst-localization-form')) {
    customElements.define(
      'mst-localization-form',
      class MstLocalizationForm extends HTMLElement {
        connectedCallback() {
          this.addEventListener('change', function (event) {
            var form = this.querySelector('form');
            if (form && event.target instanceof HTMLSelectElement) form.submit();
          });
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     Fade-up scroll reveal (.mst-reveal), once, motion-safe
     ------------------------------------------------------------------------ */
  var revealObserver = null;

  function revealAll(scope) {
    Array.prototype.forEach.call(
      scope.querySelectorAll('.mst-reveal:not(.is-revealed)'),
      function (el) {
        el.classList.add('is-revealed');
      }
    );
  }

  function initReveal(scope) {
    scope = scope || document;

    var prefersReduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced || !('IntersectionObserver' in window)) {
      revealAll(scope);
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
      );
    }

    Array.prototype.forEach.call(
      scope.querySelectorAll('.mst-reveal:not(.is-revealed)'),
      function (el) {
        revealObserver.observe(el);
      }
    );
  }

  /* ------------------------------------------------------------------------
     Delivery date "kod tebe do 20. 10." (theme-code-15). Shopify can serve a
     cached page rendered on an earlier day, so the date printed by
     snippets/mst-delivery-eta.liquid is recomputed here with the same rules
     and inputs: whole working days counted from tomorrow (shop time),
     skipping Saturdays, Sundays and the holiday list ("MM-DD" or
     "YYYY-MM-DD"), at most 60 calendar days. The server date is the no-JS
     fallback and stays if anything here is missing.
     ------------------------------------------------------------------------ */
  function pad2(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function etaDate(total, holidays, tz) {
    var m = /^([+-])(\d{2})(\d{2})$/.exec(tz || '');
    if (!m || !(total > 0)) return '';
    var offset = (m[1] === '-' ? -1 : 1) * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10));
    var shopNow = new Date(Date.now() + offset * 60000);
    var cursor = Date.UTC(shopNow.getUTCFullYear(), shopNow.getUTCMonth(), shopNow.getUTCDate());
    var counted = 0;
    var day = null;
    for (var i = 1; i <= 60; i++) {
      if (counted >= total) break;
      cursor += 86400000;
      day = new Date(cursor);
      var md = pad2(day.getUTCMonth() + 1) + '-' + pad2(day.getUTCDate());
      var dow = day.getUTCDay();
      var off =
        dow === 0 ||
        dow === 6 ||
        holidays.indexOf(md) !== -1 ||
        holidays.indexOf(day.getUTCFullYear() + '-' + md) !== -1;
      if (!off) counted += 1;
    }
    if (!day || counted < total) return '';
    return day.getUTCDate() + '. ' + (day.getUTCMonth() + 1) + '.';
  }

  function initEta(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-mst-eta]'), function (el) {
      var server = el.getAttribute('data-mst-eta');
      var fresh = etaDate(
        parseInt(el.getAttribute('data-eta-days'), 10),
        (el.getAttribute('data-eta-holidays') || '').split(','),
        el.getAttribute('data-eta-tz')
      );
      if (!server || !fresh || fresh === server || el.textContent.indexOf(server) === -1) return;
      el.textContent = el.textContent.replace(server, fresh);
      el.setAttribute('data-mst-eta', fresh);
    });
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */
  /* ------------------------------------------------------------------------
     accessibility-06 (WCAG 2.4.11) — bars fixed to the bottom of the screen
     (Shopify's cookie banner, the mobile sticky add-to-cart) must never hide
     the element that has keyboard focus. Their live height is kept in
     --mst-covered-bottom, which mst-base.css turns into the page's
     scroll-padding-bottom, so the browser's own focus scrolling stops short
     of them. Re-measured whenever one of them appears, hides or resizes,
     and on every focusin (synchronous — before the focus scroll happens).
     Scroll padding cannot move the end of the page, so while the cookie
     banner shows, its height also goes into --mst-consent-bottom, the
     body's bottom padding: the last footer link can scroll clear of it.
     Removed again once the banner hides (Prihvati/Odbij).
     ------------------------------------------------------------------------ */
  var coveredFrame = 0;
  var coveredBanner = null;

  function measureCoveredBottom() {
    if (coveredFrame) {
      window.cancelAnimationFrame(coveredFrame);
      coveredFrame = 0;
    }
    var covered = 0;
    var consent = 0;
    var bars = [
      document.getElementById('shopify-pc__banner'),
      document.querySelector('mst-sticky-atc.is-visible')
    ];
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      if (!bar) continue;
      var style = window.getComputedStyle(bar);
      if (style.position !== 'fixed' || style.display === 'none' || style.visibility === 'hidden') continue;
      covered = Math.max(covered, bar.offsetHeight || 0);
      if (i === 0) consent = Math.ceil(bar.getBoundingClientRect().height);
    }
    var value = covered > 0 ? Math.ceil(covered + 16) + 'px' : '0px';
    var root = document.documentElement;
    if (root.style.getPropertyValue('--mst-covered-bottom') !== value) {
      root.style.setProperty('--mst-covered-bottom', value);
    }
    var consentValue = consent > 0 ? consent + 'px' : '';
    if (root.style.getPropertyValue('--mst-consent-bottom') !== consentValue) {
      if (consentValue) {
        root.style.setProperty('--mst-consent-bottom', consentValue);
      } else {
        root.style.removeProperty('--mst-consent-bottom');
      }
    }
  }

  function scheduleCoveredBottom() {
    if (coveredFrame) return;
    coveredFrame = window.requestAnimationFrame(measureCoveredBottom);
  }

  function hookCoveredBanner() {
    var banner = document.getElementById('shopify-pc__banner');
    if (banner && banner !== coveredBanner) {
      coveredBanner = banner;
      new MutationObserver(scheduleCoveredBottom).observe(banner, {
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden']
      });
      if ('ResizeObserver' in window) {
        new ResizeObserver(scheduleCoveredBottom).observe(banner);
      }
    }
    scheduleCoveredBottom();
  }

  function initCoveredBottom() {
    document.addEventListener('focusin', measureCoveredBottom);
    window.addEventListener('resize', scheduleCoveredBottom);
    if ('MutationObserver' in window) {
      // Shopify inserts the banner as the first child of <body>, late.
      new MutationObserver(hookCoveredBanner).observe(document.body, { childList: true });
      // Drawers hide the sticky bar via html.mst-scroll-lock.
      new MutationObserver(scheduleCoveredBottom).observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class']
      });
      document.querySelectorAll('mst-sticky-atc').forEach(function (bar) {
        new MutationObserver(scheduleCoveredBottom).observe(bar, {
          attributes: true,
          attributeFilter: ['class']
        });
      });
    }
    hookCoveredBanner();
  }

  function boot() {
    initReveal(document);
    initEta(document);
    initCoveredBottom();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-scan newly (re)loaded sections for reveal targets.
  document.addEventListener('shopify:section:load', function (event) {
    initReveal(event.target);
    initEta(event.target);
  });
})();
